import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '../../../../../core/services/api.service';
import {
  CreateLiveCallModelPayload,
  LiveCallModel,
  LiveCallModelListResponse,
  PatchLiveCallModelPayload,
} from './live-call-models.models';

/**
 * HTTP client for the live-call voice-model catalog.
 *
 * ── BACKEND CONTRACT (prism-backend-nodejs, live-call-management/agent) ─────
 *   GET    /live-call-agent/models?includeInactive=0|1
 *            → { count, models: LiveCallModel[] }   (default model first)
 *   POST   /live-call-agent/models
 *            body: { provider, model_code, display_name, modality?, notes?, is_active? }
 *            → { model: LiveCallModel }   (409 if model_code exists)
 *   PATCH  /live-call-agent/models/:id
 *            body: { provider?, model_code?, display_name?, modality?, notes?,
 *                    is_active?, is_default? (true only — transfers the default) }
 *            → { model: LiveCallModel }
 *   DELETE /live-call-agent/models/:id
 *            → { deleted: boolean }   (the current default is protected)
 *
 * Product-agnostic: one catalog serves every product's prompt picker.
 */
@Injectable({
  providedIn: 'root',
})
export class LiveCallModelsService {
  private api = inject(ApiService);

  /** Suppress ApiService's global toaster/loader — the tab renders its own states. */
  private readonly quiet = { showErrorToaster: false, showLoader: false } as const;

  /** GET the model catalog. `includeInactive=false` keeps only selectable models. */
  listModels(includeInactive = true): Observable<LiveCallModelListResponse> {
    const params: Record<string, string | number> = {};
    if (!includeInactive) params['includeInactive'] = 0;
    return this.api
      .get<LiveCallModelListResponse>('/live-call-agent/models', params, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** POST a new model — 409 if the code is taken. */
  createModel(payload: CreateLiveCallModelPayload): Observable<{ model: LiveCallModel }> {
    return this.api
      .post<{ model: LiveCallModel }>('/live-call-agent/models', payload, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** PATCH model fields; `is_default: true` makes it the catalog default. */
  patchModel(modelId: number, payload: PatchLiveCallModelPayload): Observable<{ model: LiveCallModel }> {
    return this.api
      .patch<{ model: LiveCallModel }>(`/live-call-agent/models/${modelId}`, payload, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** DELETE (soft) one model. The current default is protected. */
  deleteModel(modelId: number): Observable<{ deleted: boolean }> {
    return this.api
      .delete<{ deleted: boolean }>(`/live-call-agent/models/${modelId}`, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** Normalize ApiService errors to the `{status?, message}` the tab renders. */
  private handleError = (error: any): Observable<never> => {
    const message =
      error?.message ||
      error?.error ||
      error?.err ||
      error?.detail ||
      'An unexpected error occurred';
    return throwError(() => ({ status: error?.statusCode ?? error?.status, message, raw: error }));
  };
}
